import React, { useState, useContext } from "react";
import Ctx from "../../contexts/AppContext";
import { bdg } from "../../constants/styles";

// Map raw mth code → human-friendly label. "razorpay" is preserved for
// legacy records (real online-gateway payments tenants made when the
// rent-payment integration was active) but rendered as "Online
// (Razorpay)" so the agent / accountant can tell what the channel was
// without seeing a defunct brand name in caps.
const _METHOD_LABELS = {
  cash: "Cash",
  upi: "UPI",
  bank: "Bank Transfer",
  cheque: "Cheque",
  razorpay: "Online (Razorpay)",
};
function methodLabel(m) {
  if (!m) return "—";
  return _METHOD_LABELS[m] || String(m).toUpperCase();
}

// Build a plain-text receipt for one transaction. Used by the Share
// button — small enough to fit a WhatsApp message, readable as-is.
function _buildReceiptText(tx, payment, tenant, propertyAddr, t, stLabel) {
  const lines = [
    "🧾 Payment Receipt",
    "──────────────",
  ];
  if (tenant?.name) lines.push(`Tenant : ${tenant.name}`);
  if (propertyAddr) lines.push(`Property: ${propertyAddr}`);
  if (payment?.mo) lines.push(`Month  : ${payment.mo}`);
  lines.push(`Amount : ₹${Number(tx.amt || 0).toLocaleString('en-IN')}`);
  lines.push(`Method : ${methodLabel(tx.mth)}`);
  lines.push(`Date   : ${tx.dt || "—"}`);
  lines.push(`Status : ${(stLabel[tx.status] || tx.status || "").replace(/[✓✕⏳]\s?/g, '')}`);
  const typeLabel = tx.type === "full" ? (t.fullPay || "Full Payment")
    : tx.type === "advance" ? (t.advancePay || "Advance Payment")
    : (t.partialPay || "Partial Payment");
  lines.push(`Type   : ${typeLabel}`);
  if (tx.totalWas > 0 && tx.totalWas !== tx.amt) {
    lines.push(`(Total bill: ₹${Number(tx.totalWas).toLocaleString('en-IN')})`);
  }
  lines.push("──────────────");
  lines.push("via RentFlow Pro");
  return lines.join("\n");
}

// Try the native share sheet (mobile / Edge). If unavailable or denied,
// open WhatsApp Web with the message pre-filled. Last-resort fallback:
// copy to clipboard and flash a notice.
async function _shareReceipt(text, flash, t) {
  // 1. Native share sheet — mobile + some desktop browsers
  if (navigator.share) {
    try {
      await navigator.share({ title: "Payment Receipt", text });
      return;
    } catch (e) {
      // User cancelled — silently exit, don't fall through to other methods
      if (e?.name === "AbortError") return;
    }
  }
  // 2. WhatsApp Web fallback
  try {
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  } catch (e) { /* fall through */ }
  // 3. Clipboard copy as last resort
  try {
    await navigator.clipboard.writeText(text);
    flash?.(t.receiptCopied || "Receipt copied to clipboard");
  } catch (e) {
    flash?.(t.shareFailed || "Could not share receipt", "error");
  }
}

function TxnHistory({ txns, payment }) {
  const { t, h, tens, props, flash } = useContext(Ctx);
  const [open, setOpen] = useState(false);
  if (!txns || txns.length === 0) return null;

  const stColor = { approved: h.gn, verified: h.gn, pending: h.wn, rejected: h.rd };
  const stLabel = { approved: "✓ " + t.approved, verified: "✓ Verified", pending: "⏳ " + t.pending, rejected: "✕ " + t.rejected };

  // Resolve tenant + property for the share message. Optional — the share
  // button still works without these (just omits those lines).
  const tenant = payment?.tid ? tens?.find(x => x.id === payment.tid) : null;
  const propertyAddr = (() => {
    if (!payment?.tid || !props) return "";
    const prop = props.find(p => p.tid === payment.tid);
    return prop?.addr || "";
  })();

  const onShare = (tx) => {
    const text = _buildReceiptText(tx, payment, tenant, propertyAddr, t, stLabel);
    _shareReceipt(text, flash, t);
  };

  return <div style={{ marginTop: 10, borderTop: `1px solid ${h.bd}`, paddingTop: 10 }}>
    <button onClick={() => setOpen(!open)} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4, padding: 0 }}>
      {open ? "▾" : "▸"} {t.payHistory} ({txns.length})
    </button>
    {open && <div style={{ marginTop: 8 }}>
      {txns.slice().reverse().map((tx, i) => <div key={tx.id || i} style={{ background: `${stColor[tx.status] || h.sc}08`, borderRadius: 10, padding: 10, marginBottom: 6, borderLeft: `3px solid ${stColor[tx.status] || h.sc}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>₹{tx.amt.toLocaleString()}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={bdg(`${stColor[tx.status] || h.sc}20`, stColor[tx.status] || h.sc)}>{stLabel[tx.status] || tx.status}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onShare(tx); }}
              title={t.shareReceipt || "Share receipt"}
              aria-label={t.shareReceipt || "Share receipt"}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "2px 6px",
                color: h.ac,
                fontSize: 14,
                lineHeight: 1,
                borderRadius: 6,
              }}
            >
              📤
            </button>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}><span style={{ color: h.sc, fontSize: 11 }}>{t.method}</span><span style={{ color: h.tx, fontSize: 11 }}>{methodLabel(tx.mth)}</span></div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}><span style={{ color: h.sc, fontSize: 11 }}>{t.date}</span><span style={{ color: h.tx, fontSize: 11 }}>{tx.dt}</span></div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}><span style={{ color: h.sc, fontSize: 11 }}>{t.type}</span><span style={{ color: tx.type === "full" ? h.gn : tx.type === "advance" ? h.ac : h.wn, fontSize: 11, fontWeight: 600 }}>{tx.type === "full" ? t.fullPay : tx.type === "advance" ? (t.advancePay || "Advance Payment") : t.partialPay}</span></div>
        {tx.status === "approved" && tx.approvedAt && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}><span style={{ color: h.sc, fontSize: 11 }}>{t.approvedOn}</span><span style={{ color: h.gn, fontSize: 11 }}>{tx.approvedAt}</span></div>}
        {tx.status === "approved" && tx.remaining > 0 && <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: h.sc, fontSize: 11 }}>{t.remaining}</span><span style={{ color: h.rd, fontSize: 11, fontWeight: 600 }}>₹{tx.remaining.toLocaleString()}</span></div>}
        {tx.status === "rejected" && tx.rejectedAt && <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: h.sc, fontSize: 11 }}>{t.rejectedOn}</span><span style={{ color: h.rd, fontSize: 11 }}>{tx.rejectedAt}</span></div>}
        {tx.totalWas > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}><span style={{ color: h.sc, fontSize: 10 }}>{t.totalWas}</span><span style={{ color: h.sc, fontSize: 10 }}>₹{tx.totalWas.toLocaleString()}</span></div>}
      </div>)}
    </div>}
  </div>;
}

export default TxnHistory;
